"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.etsPolygonWidth = exports.approximatePolygonWidth = void 0;
const turf_1 = require("@turf/turf");
const geojson_converters_1 = require("../geojson-converters");
/**
 * Returns the width of a polygon. This works best on polygons that are long and narrow
 * and of consistent width.
 * If the width varies lot the output is sane but not useful.
 *
 * There are mathematical conditions that map could potentially result in this function returning
 * undefined. These have not been fully analyzed beyond determining that they probably do not
 * not occur for any valid positive area polygon.
 *
 * There is a fairly sizeable write-up of the mathematics here:
 *     https://carboncrop.atlassian.net/wiki/spaces/CAR/pages/136675605/Calculating+Polygon+Width
 * The method was originally based on the stackoverflow question here:
 *     https://gis.stackexchange.com/questions/20279/calculating-average-width-of-polygon
 *
 * @param feature A Turf/geojson feature to calculate the width of
 * @returns The width or undefined if the width cannot be calculated
 */
function approximatePolygonWidth(feature) {
    const perimeterM = (0, turf_1.length)(feature) * 1000.0;
    const areaSqM = (0, turf_1.area)(feature);
    // Approximating the width can be done by relating area and perimeter.
    const discriminant = perimeterM ** 2 - 4 * Math.PI * areaSqM;
    if (discriminant < 0.0) {
        return undefined;
    }
    const approxWidth = (perimeterM - discriminant ** 0.5) / Math.PI;
    return approxWidth;
}
exports.approximatePolygonWidth = approximatePolygonWidth;
/**
 * The ETS rules allow for thin "tendrils" to not affect the
 * polygon width. So long as there is a "core area" of greater
 * than 30m width and > 1Ha, then it is "wide enough"
 *
 * @param feature A Turf/geojson feature to calculate the width of
 * @param options Options for the ETS rules
 * @param options.centralAreaWidthMeters The minimum width of an area to be considered a central area
 * @param options.centralAreaMinHa The minimum area for a central area to be considered a central area
 * @returns The width or undefined if the width cannot be calculated
 */
function etsPolygonWidth(feature, options) {
    var _a, _b;
    // Find potential core areas. A core area is > 30m wide and > 1Ha
    const buffer_meters = (_a = options === null || options === void 0 ? void 0 : options.centralAreaWidthMeters) !== null && _a !== void 0 ? _a : 15;
    const min_area_ha = (_b = options === null || options === void 0 ? void 0 : options.centralAreaMinHa) !== null && _b !== void 0 ? _b : 1;
    // Shrink and expand to get rid of tendrils and find large "core" areas
    const shrunk = (0, turf_1.buffer)(feature, -buffer_meters, { units: 'meters' });
    if (shrunk === undefined) {
        // No core areas if shrinking it made it disappear
        return approximatePolygonWidth(feature);
    }
    const grown = (0, turf_1.buffer)(shrunk, buffer_meters, { units: 'meters' });
    const potentialCoreAreas = (0, geojson_converters_1.flattenMultiFeatureCollection)((0, turf_1.featureCollection)([grown]));
    const coreAreaData = potentialCoreAreas.features.map((feature) => ({
        area: (0, turf_1.area)(feature),
        width: approximatePolygonWidth(feature) || 0,
    }));
    const actualCoreAreas = coreAreaData.filter((coreArea) => coreArea.area > min_area_ha);
    if (actualCoreAreas.length === 0) {
        // If there are no core areas, then the width is equal to the width of the overal
        // geometry
        return approximatePolygonWidth(feature);
    }
    // But if there is a core area, then we can pick the maximum width of the core areas
    // as the width of the polygon
    const maxCoreAreaWidth = actualCoreAreas.reduce((max, coreArea) => Math.max(max, coreArea.width), 0);
    return maxCoreAreaWidth;
}
exports.etsPolygonWidth = etsPolygonWidth;
